import type { ContactRow } from "./types";

export interface ProjectContacts {
  /** null = "All projects": org owners/admins, listed once per building. */
  projectId: number | null;
  projectName: string | null;
  rows: ContactRow[];
}

const projectNameCollator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

/**
 * Group the RPC's rows by project, keeping its order: the building-wide
 * ("All projects") rows first, then each project by name, then person.
 */
export function groupByProject(rows: ContactRow[]): ProjectContacts[] {
  const groups = new Map<number | null, ProjectContacts>();
  for (const row of rows) {
    let group = groups.get(row.projectId);
    if (!group) {
      group = { projectId: row.projectId, projectName: row.projectName, rows: [] };
      groups.set(row.projectId, group);
    }
    group.rows.push(row);
  }
  return [...groups.values()];
}

/**
 * The Building view keeps a section for every project the viewer can see,
 * even one with no rows: with owners/admins listed under "All projects", a
 * project whose only members are owners has nothing else to show, and its
 * section is still where a contact is added. "All projects" stays first; the
 * projects follow by name and then id, independent of input order.
 */
export function withEmptyProjects(
  groups: ProjectContacts[],
  projects: ReadonlyArray<{ id: number; name: string }>
): ProjectContacts[] {
  const present = new Set(groups.map((g) => g.projectId));
  const missing = projects
    .filter((p) => !present.has(p.id))
    .map((p): ProjectContacts => ({ projectId: p.id, projectName: p.name, rows: [] }));
  const all = groups.filter((g) => g.projectId == null);
  const byName = [...groups.filter((g) => g.projectId != null), ...missing].sort(
    (a, b) =>
      projectNameCollator.compare(a.projectName ?? "", b.projectName ?? "") ||
      (a.projectId as number) - (b.projectId as number)
  );
  return [...all, ...byName];
}

/** Rows carrying at least one of the chosen responsibilities; none chosen = all. */
export function matchesDisciplines(row: ContactRow, disciplineIds: readonly number[]): boolean {
  return disciplineIds.length === 0 || row.disciplineIds.some((id) => disciplineIds.includes(id));
}

/** Case-insensitive match on anything a person would search a phone book by. */
export function matchesSearch(row: ContactRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const digits = q.replace(/[^0-9]/g, "");
  const haystack = [
    row.fullName,
    row.company,
    row.jobTitle,
    row.accountEmail,
    row.notes,
    ...row.emails.map((e) => e.value),
    ...row.phones.map((p) => p.value),
    ...row.phones.map((p) => p.label),
    ...row.emails.map((e) => e.label),
  ]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
  if (haystack.includes(q)) return true;
  // "5551234" finds "+372 555 1234": compare phone numbers by digits alone.
  return (
    digits.length >= 3 && row.phones.some((p) => p.value.replace(/[^0-9]/g, "").includes(digits))
  );
}

/** Stable React key: members have no contact id until details are entered. */
export function rowKey(row: ContactRow): string {
  return row.contactId != null
    ? `c:${row.contactId}`
    : `u:${row.scope}:${row.projectId ?? "all"}:${row.userProfileId ?? "?"}`;
}

/**
 * The disciplines `userProfileId` is responsible for in one project: their
 * project entry plus, for an owner/admin, their building-wide one (which
 * applies to every project). Sorted, without duplicates.
 */
export function disciplinesOf(rows: readonly ContactRow[], userProfileId: number): number[] {
  const ids = new Set<number>();
  for (const row of rows) {
    if (row.userProfileId === userProfileId) row.disciplineIds.forEach((id) => ids.add(id));
  }
  return [...ids].sort((a, b) => a - b);
}

/**
 * Franklin users with project access who are responsible for any of
 * `disciplineIds`, the most overlapping first (ties keep the list's order).
 */
export function responsibleUserIds(
  rows: readonly ContactRow[],
  disciplineIds: readonly number[]
): number[] {
  if (disciplineIds.length === 0) return [];
  const overlap = new Map<number, number>();
  for (const row of rows) {
    if (row.userProfileId == null || !row.hasAccess) continue;
    const hits = row.disciplineIds.filter((id) => disciplineIds.includes(id)).length;
    if (hits > 0) {
      overlap.set(row.userProfileId, (overlap.get(row.userProfileId) ?? 0) + hits);
    }
  }
  return [...overlap.entries()].sort((a, b) => b[1] - a[1]).map(([userId]) => userId);
}
